import React from "react";
import { TabsContextProvider } from "./TabsContext";

export interface TabsProps {
    /**
     * Callback when the selected tab is requested to be changed via some DOM event: click, arrow, focus, etc.
     */
    onChange: (newSelectedId: string) => void;

    /**
     * The id of the selected tab.
     */
    selectedId: string;

    /**
     * Contents of the Tabs component.
     */
    children?: React.ReactNode;

    /**
     * This prop disables the behavior of automatically focusing the respective panel when the selected tab changes.
     *
     * This is useful in situations when the tabs are programatically changing due to other actions (e.g. typing in a search
     * input that should change tabs when the user begins typing, but maintain focus in the search input to allow the user to
     * continue typing.)
     */
    disableAutoTabFocusWhenSwitching?: boolean;

    /**
     * Whether the tab selection should be manual.
     * - true will not automatically show the respective panel for the focused tab. It will require
     *   enter/space/click to show.
     *
     * Typically, manual activation of tabs is only necessary when panels cannot be displayed
     * instantly, i.e., not all the panel content is present in the DOM.
     *
     * See the following link for more information:
     * https://github.com/w3c/aria-practices/commit/1653cef371a3c51702a454ff0134b68f128f756b#diff-9f2d77b171149e2aeae0b51f30cca0f92f6c12c8eb09aa74dc5f8b528fb00869R2446
     *
     * Please chat with your friendly neighborhood UX designer before making this decision.
     */
    manual?: boolean;
    /**
     * If true, the `TabPanel`s in this `Tabs` component will only render their children when that
     * `TabPanel`'s respective tab is active. This can be a helpful performance optimization if the
     * children of hidden tabs are complex.
     */
    onlyRenderActiveTabPanelChildren?: boolean;

    /**
     * This prop controls the appearance of the Tabs. Its text and border colors change depending on where
     * the tabs are rendered.
     */
    renderOn?: "canvas" | "work-surface";
}

export const Tabs = ({
    children,
    disableAutoTabFocusWhenSwitching = false,
    manual = false,
    onlyRenderActiveTabPanelChildren = false,
    selectedId,
    onChange,
    renderOn = "work-surface",
}: TabsProps) => {
    return (
        <TabsContextProvider
            disableAutoTabFocusWhenSwitching={disableAutoTabFocusWhenSwitching}
            manual={manual}
            onChange={onChange}
            onlyRenderActiveTabPanelChildren={onlyRenderActiveTabPanelChildren}
            renderOn={renderOn}
            selectedId={selectedId}
        >
            {children}
        </TabsContextProvider>
    );
};
